import React, { useEffect, useState } from "react";
import { Search, Flower2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ROSE_COLOR_HEX } from "@/lib/roses";
import { RoseGiftDialog } from "@/components/RoseGiftDialog";

export function NotePicker() {
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/notes", { params: { search: search || undefined } })
        .then((r) => setNotes(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="mt-14 card-glass rounded-3xl p-6 sm:p-10 max-w-4xl mx-auto border border-pink-500/20" data-testid="note-picker">
      <h2 className="font-serif text-3xl text-pink-100">Gift a bouquet</h2>
      <p className="text-slate-400 mt-2">Find a note by name or words, pick it, then choose your roses.</p>
      <div className="relative mt-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400" />
        <input
          data-testid="note-picker-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a recipient or a note…"
          className="w-full bg-black/40 border border-pink-500/25 rounded-full pl-12 pr-4 py-3.5 text-pink-50 placeholder:text-slate-500 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-500/30"
        />
      </div>
      <div className="mt-5 max-h-96 overflow-y-auto space-y-2 pr-1" data-testid="note-picker-list">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
        ) : notes.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="note-picker-empty">No notes match that search.</p>
        ) : (
          notes.map((n) => <PickerRow key={n.id} note={n} onPick={() => setPicked(n)} />)
        )}
      </div>
      {picked && <RoseGiftDialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)} note={picked} />}
    </div>
  );
}

function PickerRow({ note, onPick }) {
  const full = (note.roses?.length || 0) >= 3;
  return (
    <button
      data-testid={`note-picker-item-${note.id}`}
      onClick={onPick}
      disabled={full}
      className="w-full text-left flex items-center gap-4 bg-black/30 hover:bg-pink-500/10 border border-pink-500/15 hover:border-pink-400/50 rounded-2xl px-4 py-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <div className="flex-1 min-w-0">
        <p className="text-pink-100 font-serif text-lg">For {note.recipient_name}</p>
        <p className="text-slate-400 text-sm truncate italic">“{note.message}”</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {note.roses?.map((c, i) => <Flower2 key={i} className="w-4 h-4" style={{ color: ROSE_COLOR_HEX[c] }} />)}
        <span className="text-xs text-pink-300 ml-2">{full ? "Full" : "Pick"}</span>
      </div>
    </button>
  );
}
